import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, YAxis, Tooltip } from 'recharts';

/**
 * Compact inline market price-trend card rendered under an assistant message
 * when Astrid answers an energy-market question. Each feed shows its latest
 * value, period-over-period % change, and a small sparkline of recent points.
 * Degrades to nothing when no feed carries a usable series (agent text stays).
 */
const fmtVal = (v, currency) => {
  if (v == null || isNaN(v)) return '—';
  const n = Number(v);
  const money = currency ? `${currency === 'USD' ? '$' : ''}` : '';
  return `${money}${n.toFixed(2)}`;
};

const ChangePill = ({ pct }) => {
  if (pct == null || isNaN(pct)) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] text-slate-400">
        <Minus className="h-3 w-3" /> —
      </span>
    );
  }
  const up = pct > 0;
  const flat = pct === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const cls = flat ? 'text-slate-500' : up ? 'text-rose-600' : 'text-emerald-600';
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${cls}`}>
      <Icon className="h-3 w-3" />
      {up ? '+' : ''}{Number(pct).toFixed(1)}%
    </span>
  );
};

export const MarketChartCard = ({ chart }) => {
  const feeds = (chart?.feeds || []).filter((f) => (f.sparkline || []).length >= 2);
  if (!feeds.length) return null;

  return (
    <div data-testid="ai-market-chart" className="mt-3 not-prose space-y-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">Market trend</div>
      {feeds.map((f) => {
        const up = (f.change_pct ?? 0) > 0;
        const stroke = up ? '#e11d48' : '#059669';
        const series = (f.sparkline || []).map((p) => ({ x: p.period, y: Number(p.value) }));
        return (
          <div
            key={f.symbol || f.name}
            data-testid="ai-market-chart-feed"
            className="rounded-xl border border-slate-200 bg-white px-3 py-2"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-[12px] font-semibold text-slate-800">{f.name || f.hub}</div>
                <div className="truncate text-[10px] text-slate-400">
                  {f.hub}{f.commodity ? ` · ${f.commodity}` : ''}{f.unit ? ` · ${f.unit}` : ''}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[13px] font-bold text-slate-900">{fmtVal(f.latest_value, f.currency)}</div>
                <ChangePill pct={f.change_pct} />
              </div>
            </div>
            <div className="mt-1.5 h-10 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                  <YAxis hide domain={['dataMin', 'dataMax']} />
                  <Tooltip
                    cursor={{ stroke: '#cbd5e1', strokeWidth: 1 }}
                    contentStyle={{ fontSize: 11, padding: '2px 6px', borderRadius: 6 }}
                    labelFormatter={(l) => l}
                    formatter={(v) => [fmtVal(v, f.currency), 'Price']}
                  />
                  <Line type="monotone" dataKey="y" stroke={stroke} strokeWidth={1.75} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {f.latest_period && (
              <div className="mt-0.5 text-right text-[9px] text-slate-400">as of {f.latest_period}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MarketChartCard;
